"use client"

import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import Image from "next/image"
import { useInView } from "react-intersection-observer"
import { transitionDelayTime, TransitionUp } from "ui"
import { sharedColors } from "ui/theme/colors"
import {
  delayTime,
  transitionEase,
  transitionTime,
  useInViewConfig,
} from "ui/transitions/utils"
import { contactUsClick, extractProperty, getNonUmbracoUrl, Link } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 32px;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 48px;
    gap: 48px;
  }
`
const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$inView",
})<{ $inView: boolean }>`
  opacity: ${(p) => (p.$inView ? 1 : 0)};
  transition: all ${transitionTime}s ${transitionEase};
  transform: ${(p) => (p.$inView ? "translateX(0px)" : `translateX(-100vw)`)};
  transition-delay: ${delayTime}s;
  p {
    margin: 0;
  }
`

const BoxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    grid-template-columns: repeat(4, 1fr);
    gap: 32px;
  }
`

const BoxGridItem = styled(Link)`
  display: flex;
  width: 100%;
  height: 162px;
  padding: 24px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  border-radius: 12px;
  border: 1px solid ${sharedColors.borderGray};
  text-decoration: none;
  text-align: center;
  :hover {
    box-shadow: 0px 2px 8px 0px rgba(38, 48, 61, 0.16);
  }
`

export default function InformationBlocks({ data }: { data: any }) {
  const contactBoxesBlock = extractProperty(data, "contactBoxs") || []

  const title = extractProperty(contactBoxesBlock[0], "contactUsTitle")
  const boxItemsBlock = extractProperty(contactBoxesBlock[0], "boxItems") || []

  const infoIconListItems = boxItemsBlock.map((item: any) => ({
    icon: extractProperty(item, "icon"),
    text: extractProperty(item, "linktocontact")?.[0]?.name,
    url: extractProperty(item, "linktocontact")?.[0]?.url,
  })) as { icon: string; text: string; url: string }[]

  const { ref, inView } = useInView(useInViewConfig)
  return (
    <StyledContainer ref={ref}>
      <StyledTypography
        $inView={inView}
        variant="h3"
        {...({ component: "h2" } as any)}
      >
        {title}
      </StyledTypography>
      <BoxGrid>
        {infoIconListItems.map((el, i) => (
          <TransitionUp
            key={i}
            $inView={inView}
            $delay={transitionDelayTime * 0.5 * i}
          >
            <BoxGridItem
              href={getNonUmbracoUrl(el.url) || ""}
              key={i}
              onClick={() => contactUsClick(el.text, "עמוד פנו אלינו")}
            >
              <Image
                src={getUmbracoMediaUrl(el.icon)}
                alt={el.text}
                width={56}
                height={56}
              />
              <Typography variant="body1" style={{ textAlign: "center" }}>
                {el.text}
              </Typography>
            </BoxGridItem>
          </TransitionUp>
        ))}
      </BoxGrid>
    </StyledContainer>
  )
}
